<template>
  <div class="cart-list">
    <div
      v-for="(item, index) in defineStore.cart"
      :key="
        item.id +
        '-' +
        (item.variant ? Object.values(item.variant.options).join('-') : index)
      "
      class="cart-item"
      :class="{
        'cart-item--picking': pickingStampReward && !item.isStampReward,
        'cart-item--reward': !!item.isStampReward,
      }"
      :role="pickingStampReward && !item.isStampReward ? 'button' : undefined"
      :tabindex="pickingStampReward && !item.isStampReward ? '0' : undefined"
      :aria-pressed="
        pickingStampReward && !item.isStampReward ? 'false' : undefined
      "
      @click="onRowClick(index, item)"
      @keydown.enter.prevent="onRowKeySelect(index, item)"
      @keydown.space.prevent="onRowKeySelect(index, item)"
    >
      <!-- product thumbnail -->
      <span class="cart-item__thumb">
        <app-img
          v-if="item.item.img"
          class="cart-item__img"
          :img="item.item.img"
          height="48"
          width="48"
        />
        <v-icon v-else size="16" color="rgba(100,116,139,0.6)"
          >fal fa-shopping-bag</v-icon
        >
      </span>

      <!-- name column: name, reward/variant chips, unit-price caption -->
      <div class="cart-item__name-col">
        <p class="cart-item__name" :title="item.item.name">
          {{ item.item.name }}
        </p>

        <v-chip
          v-if="item.isStampReward"
          x-small
          color="amber darken-2"
          text-color="white"
          class="reward-chip font-weight-bold"
        >
          <v-icon left x-small>fal fa-stamp</v-icon>
          {{ $t("stamp_paid_by_stamp") }}
        </v-chip>

        <!-- variant / variation chips -->
        <div v-if="hasVariants(item)" class="cart-item__chips">
          <v-chip
            v-if="item.variant && item.variant.options"
            x-small
            color="grey"
            outlined
            class="rounded-lg text-capitalize pa-1"
          >
            {{ formatVariantOptions(item.variant.options) }}
          </v-chip>
          <template
            v-else-if="
              (item.selectedVariationOptions &&
                Object.keys(item.selectedVariationOptions).length > 0) ||
              (item.variationOptions &&
                Object.keys(item.variationOptions).length > 0)
            "
          >
            <v-chip
              v-for="(option, key) in item.selectedVariationOptions ||
              item.variationOptions"
              :key="key"
              x-small
              color="grey"
              outlined
              class="mr-1 rounded-lg text-capitalize"
            >
              {{ key }}: {{ option.value || option.name || option }}
            </v-chip>
          </template>
        </div>

        <p
          v-if="!item.isStampReward && !isBuffetIncludedProduct(item.item)"
          class="cart-item__unit-note"
        >
          {{ originalPrice(item)
          }}{{ item.unitAmount ? ` × ${item.unitAmount}` : "" }}
        </p>
      </div>

      <!-- right column: line total (or free/reward state) + qty stepper -->
      <div class="cart-item__right-col">
        <template v-if="item.isStampReward">
          <span class="cart-item__free-total">{{ $t("free") }}</span>
          <button
            class="cart-item__reward-remove"
            :aria-label="$t('stamp_remove_reward')"
            @click.stop="$emit('removeStampReward')"
          >
            <v-icon x-small left>fal fa-times</v-icon>
            {{ $t("stamp_remove_reward") }}
          </button>
        </template>
        <template v-else-if="isBuffetIncludedProduct(item.item)">
          <v-chip x-small label outlined color="primary" class="rounded-lg">
            {{ $t("free") }}
          </v-chip>
        </template>
        <template v-else>
          <p class="cart-item__total">{{ itemTotalPrice(item) }}</p>
          <!-- quantity stepper + remove -->
          <div class="cart-item__controls">
            <div class="cart-item__qty">
              <button
                class="cart-item__qty-btn"
                :aria-label="$t('minus') || 'Decrease'"
                @click.stop="decrementCart(item)"
              >
                −
              </button>
              <span class="cart-item__qty-text">{{ item.qty }}</span>
              <button
                class="cart-item__qty-btn"
                :aria-label="$t('plus') || 'Increase'"
                @click.stop="incrementCart(item)"
              >
                +
              </button>
            </div>
            <button
              class="cart-item__remove-btn"
              :aria-label="$t('remove') || 'Remove'"
              :title="$t('remove') || 'Remove'"
              @click.stop="removeCartItem(item)"
            >
              <v-icon size="12">fal fa-times</v-icon>
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { myMixin } from "@/mixin/main";
import { mainStore } from "@/stores/modules/cart";

export default {
  mixins: [myMixin],
  props: {
    pickingStampReward: { type: Boolean, default: false },
  },
  data: () => ({
    defineStore: mainStore(),
  }),
  methods: {
    onRowClick(index, item) {
      if (this.pickingStampReward && !item.isStampReward) {
        this.$emit("pickStampReward", index);
      }
    },
    onRowKeySelect(index, item) {
      if (this.pickingStampReward && !item.isStampReward) {
        this.$emit("pickStampReward", index);
      }
    },
    removeCartItem(item) {
      this.defineStore.removeCart({ payload: item });
    },
    async decrementCart(data) {
      this.qty = this.qty - 1;
      const body = {
        payload: data,
      };
      this.defineStore.decrementCart(body);
    },
    async incrementCart(cartItem) {
      const body = {
        that: this,
        payload: cartItem.item,
        selectedVariationOptions: cartItem.variationOptions,
        unitAmount: cartItem.unitAmount,
        packQty: cartItem.packQty,
      };
      this.defineStore.addToCart(body);
    },
    hasVariants(item) {
      return (
        (item.variant && item.variant.options) ||
        (item.selectedVariationOptions &&
          Object.keys(item.selectedVariationOptions).length > 0) ||
        (item.variationOptions && Object.keys(item.variationOptions).length > 0)
      );
    },
    formatVariantOptions(options) {
      if (!options || typeof options !== "object") return "";
      return Object.entries(options)
        .map(([key, value]) => `${key}: ${value}`)
        .join(", ");
    },
    originalPrice(item) {
      const unitPrice = item.qty > 0 ? item.price / item.qty : 0;
      const format = this.$options.filters.NumberFormat(unitPrice);
      const currencySymbol =
        (item.currency && item.currency.symbol) ||
        (item.item && item.item.currency && item.item.currency.symbol) ||
        (this.defineStore.currencySymbol &&
          this.defineStore.currencySymbol.symbol) ||
        "₭";
      return `${format} ${currencySymbol}`;
    },

    itemTotalPrice(item) {
      const format = this.$options.filters.NumberFormat(item.price);
      const currencySymbol =
        (item.currency && item.currency.symbol) ||
        (item.item && item.item.currency && item.item.currency.symbol) ||
        (this.defineStore.currencySymbol &&
          this.defineStore.currencySymbol.symbol) ||
        "₭";
      return `${format} ${currencySymbol}`;
    },
  },
};
</script>

<style scoped>
/* ============ PosSmart v2 cart item row (flat list) ============ */
.cart-list {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 8px 12px;
}

.cart-item {
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
  background: #fff;
  border-bottom: 1px solid #eef0f4;
  padding: 6px 0;
  cursor: default;
  transition: background-color 0.15s ease;
}

.cart-item:hover {
  background-color: #fafafc;
}

/* thumbnail */
.cart-item__thumb {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: #f1f2f7;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.cart-item__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
}

.cart-item__remove-btn {
  width: 24px;
  height: 24px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #64748b; /* slate-500, matches qty-btn */
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.cart-item__remove-btn:hover {
  background: #fee2e2; /* red-100 */
  color: #dc2626;
}

.cart-item__remove-btn:focus-visible {
  outline: 2px solid #5b39c9;
  outline-offset: 2px;
}

.cart-item__name-col {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.cart-item__name {
  font-size: 13px;
  font-weight: 500;
  line-height: 1.25;
  color: #141021;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 5px;
}

.cart-item__chips {
  display: flex;
  flex-wrap: wrap;
  margin-top: 2px;
}

.cart-item__unit-note {
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8; /* slate-400 */
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cart-item__right-col {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
}

.cart-item__total {
  font-size: 13.5px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: #141021;
  white-space: nowrap;
}

/* qty stepper + remove, side by side */
.cart-item__controls {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* qty pill stepper — one unified pill instead of 3 separate boxes */
.cart-item__qty {
  display: flex;
  align-items: center;
  height: 24px;
  border-radius: 8px;
  background: #f8fafc;
  flex-shrink: 0;
  overflow: hidden;
}

.cart-item__qty-btn {
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 8px;
  background: transparent;
  font-weight: 400;
  font-size: 13px;
  line-height: 1;
  color: #64748b; /* slate-500 */
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.cart-item__qty-btn:hover {
  background: #ede9fe;
  color: #5b39c9;
}

.cart-item__qty-btn:focus-visible {
  outline: 2px solid #5b39c9;
  outline-offset: 2px;
}

.cart-item__qty-text {
  min-width: 22px;
  height: 24px;
  text-align: center;
  line-height: 24px;
  font-weight: 400;
  font-size: 14px;
  color: #141021;
  background: #fff;
}

/* stamp reward state */
.cart-item--picking {
  cursor: pointer;
  background-color: rgba(245, 158, 11, 0.06);
  outline: 1px dashed rgba(245, 158, 11, 0.45);
  outline-offset: -2px;
}

.cart-item--picking:hover,
.cart-item--picking:focus-visible {
  background-color: rgba(245, 158, 11, 0.14);
  outline: 2px solid rgba(245, 158, 11, 0.6);
  outline-offset: -2px;
}

.cart-item--reward {
  background: linear-gradient(
    90deg,
    rgba(245, 158, 11, 0.1),
    rgba(245, 158, 11, 0.02)
  );
}

.reward-chip {
  letter-spacing: 0;
  text-transform: uppercase;
  flex-shrink: 0;
}

.cart-item__free-total {
  font-weight: 800;
  font-size: 14px;
  color: #b45309;
  letter-spacing: 0.5px;
  display: inline-block;
  margin-bottom: 2px;
}

.cart-item__reward-remove {
  display: inline-flex;
  align-items: center;
  border: none;
  background: transparent;
  color: #b45309;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0;
  cursor: pointer;
}

@media (prefers-reduced-motion: reduce) {
  .cart-item {
    transition: none;
  }
}
</style>
